import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../../../services/api/axios";

const TradeShowWorkspacesContext = createContext(null);
const SAVE_DEBOUNCE_MS = 750;
const TSA_DEBUG = process.env.REACT_APP_TSA_DEBUG === "true";

function logTradeShowAgent(message, details) {
  if (!TSA_DEBUG) return;
  console.info(`[TradeShowAgent] ${message}`, details || "");
}

export function TradeShowWorkspacesProvider({ children }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const workspacesRef = useRef([]);
  const saveQueues = useRef({});
  const loadControllerRef = useRef(null);
  const mountedRef = useRef(true);

  const loadWorkspaces = useCallback(async () => {
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;

    logTradeShowAgent("workspaces load started");
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/trade-show-agent/workspaces", {
        signal: controller.signal,
      });
      if (!mountedRef.current || controller.signal.aborted) return;
      workspacesRef.current = response.data;
      setWorkspaces(response.data);
      logTradeShowAgent("workspaces load completed", { count: response.data?.length || 0 });
    } catch (requestError) {
      if (controller.signal.aborted) {
        logTradeShowAgent("workspaces load aborted");
        return;
      }
      if (mountedRef.current) {
        setError(requestError?.response?.data?.detail || "Unable to load trade show workspaces.");
      }
      logTradeShowAgent("workspaces load failed", {
        status: requestError?.response?.status,
        message: requestError?.message,
      });
    } finally {
      if (loadControllerRef.current === controller) {
        loadControllerRef.current = null;
      }
      if (mountedRef.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    logTradeShowAgent("provider mounted");
    mountedRef.current = true;
    loadWorkspaces();
    return () => {
      logTradeShowAgent("provider unmounted; cleanup started");
      mountedRef.current = false;
      loadControllerRef.current?.abort();
      Object.values(saveQueues.current).forEach((state) => {
        if (state.timer) clearTimeout(state.timer);
        state.controller?.abort();
        state.waiters?.forEach((resolve) => resolve(null));
      });
      saveQueues.current = {};
      logTradeShowAgent("provider cleanup completed");
    };
  }, [loadWorkspaces]);

  const queueSave = useCallback((id, patch) => {
    if (!id) return Promise.resolve();

    let state = saveQueues.current[id];
    if (!state) {
      state = {
        pendingPatch: {},
        timer: null,
        inFlight: null,
        controller: null,
        waiters: [],
      };
      saveQueues.current[id] = state;
    }

    state.pendingPatch = { ...state.pendingPatch, ...patch };

    const promise = new Promise((resolve) => {
      state.waiters.push(resolve);
    });

    const flush = () => {
      if (state.inFlight || !Object.keys(state.pendingPatch).length) return;

      const nextPatch = state.pendingPatch;
      const waiters = state.waiters;
      const controller = new AbortController();
      state.pendingPatch = {};
      state.waiters = [];
      state.controller = controller;

      logTradeShowAgent("workspace save started", { id, fields: Object.keys(nextPatch) });
      state.inFlight = api
        .patch(`/api/trade-show-agent/workspaces/${id}`, nextPatch, {
          signal: controller.signal,
          timeout: 120000,
        })
        .catch((requestError) => {
          if (controller.signal.aborted) {
            logTradeShowAgent("workspace save aborted", { id });
            return null;
          }
          if (mountedRef.current) {
            setError(requestError?.response?.data?.detail || "Unable to save trade show workspace changes.");
          }
          logTradeShowAgent("workspace save failed", {
            id,
            status: requestError?.response?.status,
            message: requestError?.message,
          });
          return null;
        })
        .then((response) => {
          waiters.forEach((resolve) => resolve(response));
          if (response) {
            logTradeShowAgent("workspace save completed", { id, status: response.status });
          }
          return response;
        })
        .finally(() => {
          state.inFlight = null;
          if (state.controller === controller) {
            state.controller = null;
          }
          if (!mountedRef.current) {
            delete saveQueues.current[id];
            return;
          }
          if (Object.keys(state.pendingPatch).length) {
            state.timer = setTimeout(() => {
              state.timer = null;
              flush();
            }, SAVE_DEBOUNCE_MS);
          } else {
            delete saveQueues.current[id];
          }
        });
    };

    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      state.timer = null;
      flush();
    }, SAVE_DEBOUNCE_MS);

    return promise;
  }, []);

  const updateField = useCallback(
    (id, field, value) => {
      const current = workspacesRef.current;
      const workspace = current.find((item) => item.id === id);
      if (!workspace) return Promise.resolve();
      const nextValue = typeof value === "function" ? value(workspace[field] ?? []) : value;
      const next = current.map((item) => (item.id === id ? { ...item, [field]: nextValue } : item));
      workspacesRef.current = next;
      setWorkspaces(next);
      return queueSave(id, { [field]: nextValue });
    },
    [queueSave]
  );

  const addWorkspace = useCallback(async (data) => {
    const response = await api.post("/api/trade-show-agent/workspaces", data);
    const next = [response.data, ...workspacesRef.current];
    workspacesRef.current = next;
    setWorkspaces(next);
    return response.data;
  }, []);

  const deleteWorkspace = useCallback(async (id) => {
    await api.delete(`/api/trade-show-agent/workspaces/${id}`);
    const next = workspacesRef.current.filter((workspace) => workspace.id !== id);
    workspacesRef.current = next;
    setWorkspaces(next);
  }, []);

  const getWorkspace = useCallback(
    (id) => workspaces.find((workspace) => workspace.id === id) ?? null,
    [workspaces]
  );

  const setWorkspaceExhibitorPreview = useCallback(
    (id, preview) =>
      updateField(id, "exhibitorPreview", { ...preview, importedAt: new Date().toISOString() }),
    [updateField]
  );
  const setWorkspaceCommunityProfile = useCallback(
    (id, profile) =>
      updateField(id, "communityProfile", { ...profile, updatedAt: new Date().toISOString() }),
    [updateField]
  );
  const setWorkspaceBriefs = useCallback((id, briefs) => updateField(id, "briefs", briefs), [updateField]);
  const setWorkspaceDrafts = useCallback((id, drafts) => updateField(id, "drafts", drafts), [updateField]);
  const setWorkspaceAgentRuns = useCallback(
    (id, agentRuns) => updateField(id, "agentRuns", agentRuns),
    [updateField]
  );

  return (
    <TradeShowWorkspacesContext.Provider
      value={{
        workspaces,
        loading,
        error,
        loadWorkspaces,
        addWorkspace,
        deleteWorkspace,
        getWorkspace,
        setWorkspaceExhibitorPreview,
        setWorkspaceCommunityProfile,
        setWorkspaceBriefs,
        setWorkspaceDrafts,
        setWorkspaceAgentRuns,
      }}
    >
      {children}
    </TradeShowWorkspacesContext.Provider>
  );
}

export function useTradeShowWorkspaces() {
  const ctx = useContext(TradeShowWorkspacesContext);
  if (!ctx) throw new Error("useTradeShowWorkspaces must be used inside TradeShowWorkspacesProvider");
  return ctx;
}
